// ModalTestModel.jsx
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, CircularProgress, Slider
} from '@mui/material';
import axios from 'axios'; // make sure `import './setupAxios'` runs once at app startup

// helper to build absolute URLs for images returned as relative paths
const apiURL = (path) =>
  path?.startsWith('http') ? path : `${axios.defaults.baseURL || ''}${path || ''}`;

// cache buster: ensures browser always fetches a fresh version of the image
const addCacheBuster = (url) => {
  if (!url) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}cb=${Date.now()}`;
};

const ModalTestModel = ({
  open,
  model,
  onClose,
  testImage,
  setTestImage,
  previewURL,
  setPreviewURL,
  showTestImages = true,
}) => {
  const [modelTestImages, setModelTestImages] = useState([]);
  const [currentModelTestImageIndex, setCurrentModelTestImageIndex] = useState(0);
  const [isModelTestImage, setIsModelTestImage] = useState(false);
  const [resultImageURL, setResultImageURL] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ confidence slider state (default 0.25 is common for YOLO)
  const [confidence, setConfidence] = useState(0.25);

  // load model details (gets test_images array)
  useEffect(() => {
    const fetchTestImages = async () => {
      if (!model) return;
      try {
        const { data } = await axios.get(`/models/${model.id}`);
        setModelTestImages(data.test_images || []);
        setCurrentModelTestImageIndex(0);
        setError(null);
      } catch {
        setError('Failed to load test images from backend');
      }
    };
    if (open) fetchTestImages();
  }, [model, open]);

  // load a test image (as blob via axios) and set it as the input
  const handleUseTestImage = async (index = 0) => {
    if (!modelTestImages.length) return;
    try {
      const imageURL = addCacheBuster(apiURL(modelTestImages[index]));
      const res = await axios.get(imageURL, { responseType: 'blob' });
      const blob = res.data;
      const file = new File([blob], `image${index + 1}.png`, { type: blob.type });

      setTestImage(file);
      setIsModelTestImage(true);

      if (previewURL) URL.revokeObjectURL(previewURL);
      setPreviewURL(URL.createObjectURL(file));
      setResultImageURL(null);
      setError(null);
      setCurrentModelTestImageIndex(index);
    } catch {
      setError('Failed to load test image');
    }
  };

  // 👉 auto-activate first test image when modal opens and images are available
  useEffect(() => {
    if (
      open &&
      showTestImages &&
      modelTestImages.length > 0 &&
      !testImage &&
      !previewURL
    ) {
      handleUseTestImage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, showTestImages, modelTestImages, testImage, previewURL]);

  const handleNextTestImage = () => {
    if (!modelTestImages.length) return;
    const nextIndex = (currentModelTestImageIndex + 1) % modelTestImages.length;
    handleUseTestImage(nextIndex);
  };

  const handlePrevTestImage = () => {
    if (!modelTestImages.length) return;
    const prevIndex = (currentModelTestImageIndex - 1 + modelTestImages.length) % modelTestImages.length;
    handleUseTestImage(prevIndex);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setIsModelTestImage(false);
    if (file) {
      setTestImage(file);
      if (previewURL) URL.revokeObjectURL(previewURL);
      setPreviewURL(URL.createObjectURL(file));
      setResultImageURL(null);
      setError(null);
    }
  };

  const handleCloseModal = () => {
    onClose();
    setTestImage(null);
    setPreviewURL(null);
    setResultImageURL(null);
    setError(null);
    setIsModelTestImage(false);
    // optional: reset confidence when closing
    // setConfidence(0.25);
  };

  // poll inference status with axios
  const pollInferenceResult = (inferenceId) =>
    new Promise((resolve, reject) => {
      const interval = 1000;
      const checkStatus = async () => {
        try {
          const { data } = await axios.get(`/inferences/${inferenceId}`);
          if (data.status === 'completed' && data.result_image) {
            resolve(data.result_image);
          } else if (data.status === 'failed') {
            reject(new Error('Inference failed'));
          } else {
            setTimeout(checkStatus, interval);
          }
        } catch (err) {
          reject(err);
        }
      };
      checkStatus();
    });

  // start inference with FormData via axios
  const runInference = async (modelId, imageFile, confValue) => {
    if (!imageFile) return;

    setLoading(true);
    setError(null);
    setResultImageURL(null);

    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      // ✅ PASS CONFIDENCE TO BACKEND
      // Change 'confidence' to 'conf' or whatever your backend expects.
      formData.append('conf', String(confValue));

      const { data } = await axios.post(`/models/${modelId}/infer`, formData);
      const inferenceId = data.id;
      if (!inferenceId) throw new Error('No inference_id returned');

      const resultImagePath = await pollInferenceResult(inferenceId);
      setResultImageURL(apiURL(resultImagePath));
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Inference failed');
    } finally {
      setLoading(false);
    }
  };

  if (!model) return null;

  // which button is "active"?
  const isTestSelected = showTestImages && isModelTestImage;
  const isBrowseSelected = !isModelTestImage && !!testImage;

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      fullWidth
      maxWidth="md"
      slotProps={{ paper: { sx: { border: '1px solid #cccccc', borderRadius: '12px' } } }}
    >
      <DialogTitle>Try it out - {model.name}</DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="body1" sx={{ whiteSpace: 'nowrap' }}>
            Import your own image to test the model.
          </Typography>

          {/* Test Images FIRST */}
          {showTestImages && (
            <Button
              variant={isTestSelected ? 'contained' : 'outlined'}
              sx={{
                ml: 2.5,
                textTransform: 'none',
                borderRadius: '12px',
                borderColor: '#16a4b0',
                backgroundColor: isTestSelected ? '#16a4b0' : 'transparent',
                color: isTestSelected ? 'white' : '#16a4b0',
                boxShadow: 'none',
              }}
              onClick={() => handleUseTestImage(0)}
              disabled={loading}
            >
              Test Images
            </Button>
          )}

          {/* Browse Image SECOND */}
          <Button
            variant={isBrowseSelected ? 'contained' : 'outlined'}
            component="label"
            sx={{
              ml: 2.5,
              textTransform: 'none',
              borderRadius: '12px',
              borderColor: '#16a4b0',
              backgroundColor: isBrowseSelected ? '#16a4b0' : 'transparent',
              color: isBrowseSelected ? 'white' : '#16a4b0',
              boxShadow: 'none',
            }}
            disabled={loading}
          >
            Browse Image
            <input type="file" accept="image/*" hidden onChange={handleFileChange} />
          </Button>
        </Box>

        {/* ✅ Confidence slider (always available; you can move it inside previewURL block if you prefer) */}
        <Box sx={{ mt: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Confidence threshold
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {confidence.toFixed(2)}
            </Typography>
          </Box>

          <Slider
            value={confidence}
            min={0}
            max={1}
            step={0.01}
            valueLabelDisplay="auto"
            disabled={loading}
            onChange={(_, v) => setConfidence(v)}
            marks={[
              { value: 0, label: '0' },
              { value: 0.25, label: '0.25' },
              { value: 0.5, label: '0.5' },
              { value: 0.75, label: '0.75' },
              { value: 1, label: '1' },
            ]}
            sx={{
              color: '#16a4b0',
              '& .MuiSlider-thumb': { boxShadow: 'none' },
            }}
          />
        </Box>

        {previewURL && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, gap: 2 }}>
            {showTestImages && isModelTestImage ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  onClick={handlePrevTestImage}
                  disabled={loading}
                  sx={{ minWidth: 0, fontSize: '5rem', color: '#16a4b0', background: 'none', boxShadow: 'none' }}
                >
                  ‹
                </Button>
                <Box
                  component="img"
                  src={resultImageURL || previewURL}
                  alt="Preview"
                  sx={{ maxWidth: 400, maxHeight: 400, borderRadius: 2, border: '1px solid #ddd', display: 'block' }}
                />
                <Button
                  onClick={handleNextTestImage}
                  disabled={loading}
                  sx={{ minWidth: 0, fontSize: '5rem', color: '#16a4b0', background: 'none', boxShadow: 'none' }}
                >
                  ›
                </Button>
              </Box>
            ) : (
              <Box
                component="img"
                src={resultImageURL || previewURL}
                alt="Preview"
                sx={{ maxWidth: '100%', maxHeight: 400, borderRadius: 2, border: '1px solid #ddd', display: 'block' }}
              />
            )}

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                variant="contained"
                sx={{
                  ml: 2.5,
                  mt: 1,
                  textTransform: 'none',
                  borderRadius: '12px',
                  borderColor: '#16a4b0',
                  backgroundColor: '#16a4b0',
                  color: 'white',
                  boxShadow: 'none',
                }}
                onClick={() => runInference(model.id, testImage, confidence)}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} sx={{ color: 'black' }} /> : 'Run Inference'}
              </Button>
            </Box>

            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCloseModal} disabled={loading} sx={{ color: '#00707c' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalTestModel;
